import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { AmazonAddressBook, WalletWidget } from 'amazon-pay-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useMst } from '../../../models/stores/Root';
import FormErrorLabel from '../../../components/input/FormErrorLabel';
import ShippingMethodSection from '../../../components/framework/ShippingMethodSection';
import AuthenticatedContactSection from '../../../components/framework/AuthenticatedContactSection';
import AuthenticatedPaymentSection from '../../../components/framework/AuthenticatedPaymentSection';
import AccountCreateForm from '../../../components/input/AccountCreateForm';
import CustomFieldSection from '../../../components/framework/CustomFieldSection';
import OrderComment from '../../../components/input/OrderComment';
import Button from '../../../components/input/Button';
import SectionTitle from '../../../components/content/SectionTitle';
import CustomMessage from '../../../components/content/CustomMessage';
import ApiValidationError from '../../../models/classes/ApiValidationError';
import ServiceError from '../../../models/classes/ServiceError';
import GuestContactSection from '../../../components/framework/GuestContactSection';
import { CustomerLoginFormData } from '../../../components/input/CustomerLoginForm';
import { GuestInfoFormData } from '../../../components/input/GuestInfoForm';
import InternalLink from '../../../components/input/InternalLink';
import { SiteLimitationKeys, UIConstants } from '../../../utils/constants';

export type AmazonOrderReference = {
  getAmazonOrderReferenceId: () => string;
  getAmazonContractId: () => string;
};

export type AmazonPayViewProps = {
  onLogout: () => void;
  onOrderReferenceCreate: (
    orderReference: AmazonOrderReference
  ) => Promise<void>;
  onAddressSelect: () => Promise<void>;
  onEditPayment: () => void;
  onScrollTopOfPage: () => void;
  showAmazonWidgets?: boolean;
};
type Theme = 'light' | 'dark';
type Size = 'compact' | 'normal' | 'invisible';
type Badge = 'bottomright' | 'bottomleft' | 'inline';
const AmazonPayView: React.FC<AmazonPayViewProps> = observer(
  ({
    onLogout,
    onOrderReferenceCreate,
    onAddressSelect,
    onEditPayment,
    onScrollTopOfPage,
    showAmazonWidgets = true,
  }: AmazonPayViewProps) => {
    const rootStore = useMst();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { configuration, customer, cart, currentStep } = rootStore;
    const { canPlaceOrder } = cart;
    const [formErrors, setFormErrors] = useState<string[]>([]);
    const [hasCustomFieldError, setHasCustomFieldError] = useState(false);
    const [hasContactInfoError, setHasContactInfoError] = useState(false);
    const [hasPaymentSelected, setHasPaymentSelected] = useState(false);
    const [canSubmitPayment, setCanSubmitPayment] = useState(false);
    const [showWidgets, setShowWidgets] = useState(showAmazonWidgets);
    const [isPasswordRequired, setIsPasswordRequired] = useState(
      !configuration.features.disableCustomerLogin
    );
    const [isLoginRequired] = useState(
      !configuration.features.disableCustomerLogin &&
        !configuration.features.enableGuestCheckout
    );
    const reCaptchaRef = useRef<ReCAPTCHA>(null);
    const [captchaResponse, setCaptchaResponse] = useState<string | null>(null);
    const changeCallback = async (response: string | null): Promise<void> => {
      if (configuration.reCaptcha.size === 'invisible') {
        return;
      }
      setCaptchaResponse(null);
      if (response) {
        const result = await configuration.verifyReCaptchaFlow(response);
        if (result === 200) {
          setCaptchaResponse(response);
          cart.updateSubmitToken(response);
        }
      }
    };
    const expiredCallback = () => setCaptchaResponse(null);
    const handlePasswordEntered = (password: string | null): void => {
      customer.setPassword(password);
    };

    const handlePasswordRequiredChange = (isRequired: boolean): void => {
      setIsPasswordRequired(isRequired);
      customer.updateIsAnonymous(!isRequired);
      if (!isRequired) {
        customer.setPassword(null);
      }
    };

    const handleShippingMethodChanged = async (): Promise<void> => {
      cart.setUpdatingCart(true);
      await rootStore.updateCartShippingAddressFlow();
      if (
        configuration.isEnabled(SiteLimitationKeys.STRIPE_SERVICE_ENABLED) &&
        configuration.hasStripePaymentMethodEnabled
      ) {
        await rootStore.putStripeIntentFlow();
      }
      cart.setUpdatingCart(false);
    };

    const handleApiValidationError = (error: ApiValidationError): void => {
      setFormErrors(error.extractErrorMessages());
      onScrollTopOfPage();
    };

    const handleStartNewCheckoutClicked = async (): Promise<void> => {
      await rootStore.startNewCheckout();
    };

    const handleGenericError = (error: ServiceError | Error): void => {
      setFormErrors([error.message]);
      onScrollTopOfPage();
    };
    const handleValidGuestContactSection = (
      formData: GuestInfoFormData
    ): void => {
      rootStore.setEmailRegistered(formData.emailAddress);
      cart.updateCustomer(formData);
    };
    const handleTryCustomerLoginAsync = async (
      formData: CustomerLoginFormData
    ): Promise<string[] | undefined> => {
      try {
        await rootStore.loginFlow(formData);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.isValidationError && error.errors?.length) {
          return error.errors;
        }
        // eslint-disable-next-line no-console
        console.error(error);
        return ['Unable to login at this time.'];
      }
      return undefined;
    };

    const handleLoginSuccess = async (): Promise<void> => {
      await rootStore.loadCustomerFlow();
      rootStore.send({ type: 'USER_LOGIN', customer: customer.customerData });
    };

    const handleOnPaymentSelect = async (): Promise<void> => {
      setHasPaymentSelected(true);
    };

    const handlePlaceOrder = async (): Promise<void> => {
      cart.setUpdatingCart(true);
      if (rootStore.invalidCountryShippingAddress) {
        setFormErrors([UIConstants.INVALID_COUNTRY_ERROR]);
        cart.setUpdatingCart(false);
        return;
      }

      if (rootStore.invalidStateShippingAddress) {
        setFormErrors([UIConstants.INVALID_STATE_ERROR]);
        cart.setUpdatingCart(false);
        return;
      }

      let reCaptchaStatus = 400;

      if (
        reCaptchaRef.current &&
        configuration.reCaptcha.checkoutEnabled &&
        configuration.reCaptcha.size === 'invisible'
      ) {
        cart.setUpdatingCart(false);
        cart.updateSubmitToken();
        const reCaptchaValue = await reCaptchaRef.current.executeAsync();
        if (reCaptchaValue) {
          cart.setUpdatingCart(true);
          reCaptchaStatus = await configuration.verifyReCaptchaFlow(
            reCaptchaValue
          );
        }
        reCaptchaRef.current.reset();
        if (reCaptchaStatus !== 200) {
          cart.setUpdatingCart(false);
          setFormErrors([UIConstants.RECAPTCHA_ERROR]);
          onScrollTopOfPage();
          return;
        }
        cart.updateSubmitToken(reCaptchaValue);
      }

      setFormErrors([]);

      let orderId: string | undefined;
      const newCustomer = rootStore.stateMatches('AmazonPay.UserStatus.Guest');
      try {
        if (newCustomer) {
          customer.setNewCustomerData(cart.cartData);
          const customerId = await customer.saveNewCustomer();
          cart.customer.updateId(customerId);
        }

        const orderResponse = await cart.placeOrder();
        orderId = orderResponse.orderId;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.isValidationError) {
          handleApiValidationError(error);
        } else {
          handleGenericError(error);
        }
      }
      if (orderId) {
        if (newCustomer) {
          try {
            await customer.saveCustomerAddresses();
          } catch (error) {
            // TODO: Do something with this caught error other than logging
            // eslint-disable-next-line no-console
            console.error(error);
          }
        }
        rootStore.send({ type: 'ORDER_PLACED', orderId });
      } else {
        setShowWidgets(false);
      }
      cart.setUpdatingCart(false);
    };

    useEffect(() => {
      let canProceed = true;
      if (isLoginRequired) {
        canProceed = rootStore.isEmailRegistered
          ? rootStore.customer.isLoggedIn
          : !!customer.password;
      }

      setCanSubmitPayment(
        !hasCustomFieldError &&
          !hasContactInfoError &&
          hasPaymentSelected &&
          canPlaceOrder &&
          showWidgets &&
          canProceed
      );
    }, [
      hasCustomFieldError,
      hasContactInfoError,
      hasPaymentSelected,
      canPlaceOrder,
      showWidgets,
      isLoginRequired,
      isPasswordRequired,
      customer.password,
      rootStore.isEmailRegistered,
      rootStore.customer.isLoggedIn,
    ]);

    const resetAfterFailedOrder = (): void => {
      setFormErrors([]);
      setShowWidgets(true);
    };

    return (
      <>
        <SectionTitle>Checkout: Final Step</SectionTitle>
        {rootStore.stateMatches('Amazon.UserStatus.Registered') && (
          <div className="mx-2 text-sm">
            Review, and click pay. Or{' '}
            <InternalLink onClick={handleStartNewCheckoutClicked}>
              start a new checkout
            </InternalLink>
            .
          </div>
        )}
        {!!configuration.messages.oneClick && (
          <CustomMessage customMessage={configuration.messages.oneClick} />
        )}
        <FormErrorLabel errors={formErrors} />
        {rootStore.stateMatches('AmazonPay.UserStatus.Registered') ? (
          <AuthenticatedContactSection
            emailAddress={cart.customer.emailAddress as string}
            onLogout={onLogout}
          />
        ) : (
          <GuestContactSection
            onValidData={handleValidGuestContactSection}
            onErrorStatus={setHasContactInfoError}
            defaultEmailAddress={cart.customer.emailAddress ?? undefined}
            isEmailRegistered={
              !configuration.features.disableCustomerLogin &&
              rootStore.isEmailRegistered
            }
            tryCustomerLoginAsync={
              !configuration.features.disableCustomerLogin
                ? handleTryCustomerLoginAsync
                : undefined
            }
            onLoginSuccess={handleLoginSuccess}
            loginRequired={isLoginRequired}
          />
        )}
        <div className="flex flex-col my-2">
          <SectionTitle>Choose a Shipping Address</SectionTitle>
          {showWidgets && (
            <AmazonAddressBook
              clientId={configuration.amazonPay.clientId}
              sellerId={configuration.amazonPay.sellerId}
              isSandbox={configuration.amazonPay.isSandbox}
              agreementType="orderReference"
              region="us"
              size="x-large"
              onOrderReferenceCreate={onOrderReferenceCreate}
              onAddressSelect={onAddressSelect}
            />
          )}
        </div>
        <ShippingMethodSection onValidData={handleShippingMethodChanged} />
        <div className="flex flex-col my-2">
          <SectionTitle>Choose a Payment Method</SectionTitle>
          {showWidgets ? (
            <WalletWidget
              clientId={configuration?.amazonPay?.clientId}
              sellerId={configuration?.amazonPay?.sellerId}
              isSandbox={configuration?.amazonPay?.isSandbox}
              onPaymentSelect={handleOnPaymentSelect}
              agreementType="orderReference"
              region="us"
            />
          ) : (
            <div className="w-full">
              <Button
                className="max-w-max"
                center
                onClick={resetAfterFailedOrder}
              >
                Try another payment method
              </Button>
            </div>
          )}
          <div className="mt-1">
            <AuthenticatedPaymentSection
              paymentProfile={cart.paymentProfile}
              onPaymentEdit={onEditPayment}
              appliedGiftCards={cart.cartData.giftCards}
            />
          </div>
        </div>
        <CustomFieldSection
          customFields={cart.cartData.customFields}
          onErrorStatus={setHasCustomFieldError}
          useContext={false}
        />
        {!configuration?.features?.disableCustomerLogin &&
          !rootStore.isEmailRegistered && (
            <AccountCreateForm
              needAccountToCheckout={
                !configuration.features?.enableGuestCheckout
              }
              isEmailRegistered={rootStore.isEmailRegistered}
              isPasswordRequired={isPasswordRequired}
              onPasswordEntered={handlePasswordEntered}
              onRequiredChange={handlePasswordRequiredChange}
            />
          )}
        {configuration?.features?.enableOrderComments && (
          <OrderComment
            orderComment={cart.cartData.misc?.comment || ''}
            onOrderCommentUpdated={cart.updateComment}
          />
        )}
        {configuration?.reCaptcha?.checkoutEnabled && (
          <div className="flex items-center justify-center">
            <ReCAPTCHA
              ref={reCaptchaRef}
              sitekey={configuration?.reCaptcha?.siteKey as string}
              size={configuration?.reCaptcha?.size as Size}
              hl={configuration?.reCaptcha?.hl as string}
              theme={configuration?.reCaptcha?.theme as Theme}
              badge={configuration?.reCaptcha?.badge as Badge}
              onExpired={expiredCallback}
              onChange={changeCallback}
            />
          </div>
        )}
        <Button
          center
          className="max-w-sm"
          isDisabled={
            !canSubmitPayment ||
            (configuration?.reCaptcha?.checkoutEnabled &&
              configuration?.reCaptcha?.size !== 'invisible' &&
              !captchaResponse)
          }
          isOtherActionPending={cart.isTokenizingCard || cart.updatingCart}
          onClick={handlePlaceOrder}
        >
          {`Pay ${
            configuration.formattedCurrencySymbol
          }${cart.grandTotal.toFixed(2)}`}
        </Button>
      </>
    );
  }
);

export default AmazonPayView;
